import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from '@/components/layout/Layout';
import { useProduct } from '@/hooks/useProducts';
import { useCartStore } from '@/stores/cartStore';
import { formatPrice } from '@/lib/shopify';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Minus, Plus, Loader2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import { StructuredData } from '@/components/seo/StructuredData';
import { ProductSeoHead } from '@/components/product/ProductSeoHead';
import { generateProductSeo } from '@/lib/productSeo';
import { ProductImageGallery } from '@/components/product/ProductImageGallery';
import { RelatedProducts } from '@/components/product/RelatedProducts';
import { productRedirects } from '@/data/productRedirects';

const BASE_URL = 'https://sampaolicreazioni.it';

export default function Product() {
  const { t, i18n } = useTranslation();
  const { handle } = useParams<{ handle: string }>();
  const navigate = useNavigate();
  const { data: product, isLoading: productLoading, error } = useProduct(handle || '');
  const addItem = useCartStore(state => state.addItem);
  const cartLoading = useCartStore(state => state.isLoading);
  
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);

  // Redirect old English handles to current Italian handles
  useEffect(() => {
    if (!productLoading && !product && handle) {
      const redirectHandle = productRedirects[handle];
      if (redirectHandle) {
        navigate(`/product/${redirectHandle}`, { replace: true });
      }
    }
  }, [productLoading, product, handle, navigate]);

  // Generate SEO data
  const seo = useMemo(() => {
    if (!product) return null;
    return generateProductSeo(
      {
        title: product.title,
        description: product.description || '',
        handle: product.handle,
        tags: product.tags || [],
        productType: product.productType || '',
        vendor: product.vendor || 'Sampaoli Creazioni',
        options: product.options || [],
        variants: product.variants,
        images: product.images,
      },
      (i18n.language === 'it' ? 'it' : 'en') as 'it' | 'en'
    );
  }, [product, i18n.language]);

  if (productLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-bronze" />
        </div>
      </Layout>
    );
  }

  if (error || !product) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-semibold text-foreground mb-2">
              {t('product.notFound')}
            </h1>
            <p className="text-muted-foreground">
              {t('product.notFoundDesc')}
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  const images = product.images.edges;
  const variants = product.variants.edges;
  const selectedVariant = variants[selectedVariantIndex]?.node;
  const hasMultipleVariants = variants.length > 1 && variants[0]?.node.title !== "Default Title";
  const canonicalUrl = `${BASE_URL}/product/${handle}`;

  const handleAddToCart = async () => {
    if (!selectedVariant) return;
    
    await addItem({
      product: { node: product },
      variantId: selectedVariant.id,
      variantTitle: selectedVariant.title,
      price: selectedVariant.price,
      quantity,
      selectedOptions: selectedVariant.selectedOptions || [],
    });
    
    toast.success(t('product.addedToCart'), {
      description: `${product.title} x${quantity}`,
      position: 'top-center',
    });
  };

  return (
    <Layout>
      {/* SEO Head */}
      {seo && (
        <ProductSeoHead
          title={seo.metaTitle}
          description={seo.metaDescription}
          canonicalUrl={canonicalUrl}
          imageUrl={images[0]?.node?.url || ''}
          imageAlt={seo.imageAlts[0] || product.title}
        />
      )}

      {/* Structured Data */}
      <StructuredData
        type="breadcrumb"
        items={[
          { name: 'Home', url: '/' },
          { name: t('nav.shop'), url: '/shop' },
          { name: product.title, url: `/product/${handle}` },
        ]}
      />
      <StructuredData
        type="product"
        name={product.title}
        image={images[0]?.node?.url || ''}
        description={seo?.metaDescription || product.description || product.title}
        price={selectedVariant?.price?.amount || '0'}
        currency={selectedVariant?.price?.currencyCode || 'EUR'}
        availability={selectedVariant?.availableForSale || false}
        url={canonicalUrl}
      />

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="mb-6">
          <ol className="flex items-center gap-1 text-sm text-muted-foreground">
            <li><Link to="/" className="hover:text-bronze transition-colors">Home</Link></li>
            <li><ChevronRight className="w-3 h-3" /></li>
            <li><Link to="/shop" className="hover:text-bronze transition-colors">{t('nav.shop')}</Link></li>
            <li><ChevronRight className="w-3 h-3" /></li>
            <li className="text-foreground font-medium truncate max-w-[200px]">{product.title}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {/* Images */}
          <ProductImageGallery
            images={images}
            title={product.title}
            imageAlts={seo?.imageAlts || []}
          />

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col"
          >
            <h1 className="font-display text-3xl md:text-4xl text-foreground mb-4">
              {product.title}
            </h1>
            
            <p className="text-2xl font-heading font-semibold text-bronze mb-6">
              {selectedVariant && formatPrice(selectedVariant.price.amount, selectedVariant.price.currencyCode)}
            </p>

            {/* Short excerpt from Shopify description */}
            {product.description && product.description.length > 10 && (
              <p className="font-body text-muted-foreground leading-relaxed mb-6 line-clamp-3">
                {product.description}
              </p>
            )}

            {/* Variant Selection */}
            {hasMultipleVariants && (
              <div className="mb-6">
                <label className="block font-heading text-sm font-medium text-foreground mb-2">
                  {t('product.variant')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {variants.map((variant, idx) => (
                    <button
                      key={variant.node.id}
                      onClick={() => setSelectedVariantIndex(idx)}
                      disabled={!variant.node.availableForSale}
                      className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                        selectedVariantIndex === idx
                          ? 'border-bronze bg-bronze/10 text-bronze'
                          : 'border-border text-foreground hover:border-bronze/50'
                      } ${!variant.node.availableForSale ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {variant.node.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mb-6">
              <label className="block font-heading text-sm font-medium text-foreground mb-2">
                {t('common.quantity')}
              </label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-12 text-center font-medium text-lg">{quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Add to Cart */}
            <Button
              onClick={handleAddToCart}
              disabled={!selectedVariant?.availableForSale || cartLoading}
              className="w-full bg-bronze hover:bg-bronze/90 text-ivory py-6 text-lg"
              size="lg"
            >
              {cartLoading ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <ShoppingCart className="w-5 h-5 mr-2" />
              )}
              {selectedVariant?.availableForSale ? t('common.addToCart') : t('product.unavailable')}
            </Button>

            {/* Trust Badges */}
            <div className="mt-8 pt-6 border-t border-border space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="w-2 h-2 bg-bronze rounded-full" />
                {t('product.madeInItaly')}
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="w-2 h-2 bg-bronze rounded-full" />
                {t('product.hypoallergenic')}
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="w-2 h-2 bg-bronze rounded-full" />
                {t('product.globalShipping')}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Full Shopify description below */}
        {product.descriptionHtml && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 border-t border-border pt-12 max-w-3xl"
          >
            <h2 className="font-display text-xl md:text-2xl text-foreground mb-6">
              {i18n.language === 'it' ? 'Descrizione completa' : 'Full Description'}
            </h2>
            <div
              className="font-body text-muted-foreground leading-relaxed prose prose-sm max-w-none
                [&_p]:mb-3 [&_br]:block [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5
                [&_strong]:text-foreground [&_strong]:font-medium [&_a]:text-bronze [&_a]:underline"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(product.descriptionHtml, {
                  ALLOWED_TAGS: ['p', 'br', 'ul', 'ol', 'li', 'strong', 'em', 'a', 'h2', 'h3', 'h4', 'span'],
                  ALLOWED_ATTR: ['href', 'target', 'rel'],
                  ADD_ATTR: ['rel'],
                  FORCE_BODY: true,
                }),
              }}
            />
          </motion.div>
        )}

        {/* Related Products */}
        <RelatedProducts currentHandle={product.handle} tags={product.tags || []} />
      </div>
    </Layout>
  );
}
